import { useState, useEffect, useCallback } from 'react';
import { APIProvider, AdvancedMarker, Map, MapControl, ControlPosition, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import SearchForm from './SearchForm.js';
import PropertyResult from './PropertyResult.js';
import SearchHeader from './SearchHeader.js';
import SearchPagination from './SearchPagination.js';
import MapDataOverlays from './MapDataOverlays.js';

export default function PropertySearchMapElements({ pageId, config, initialResults, initialUrl, content }) {
    const [results, setResults] = useState(initialResults);
    const formId = 'propertySearchForm';
    const [form, setForm] = useState(null);
    const [order, setSortOrder] = useState(config.OrderBy);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [ncaState, setNcaState] = useState(false);
    const [lpaState, setLpaState] = useState(false);
    const [bngLabel, setBngLabel] = useState('');

    const baseUrl = initialUrl.split('?')[0];
    const mapPageSize = 50;

    const MapBoundsComponent = (r) => {
        const map = useMap();
        const core = useMapsLibrary('core');

        useEffect(() => {
            if (!core || !map || r.results == null || !r.results.Properties || r.results.Properties.length == 0) return;

            var bounds = new core.LatLngBounds();
            for (var i = 0; i < r.results.Properties.length; i++) {
                bounds.extend({ lat: r.results.Properties[i].Lat, lng: r.results.Properties[i].Lng });
            }

            map.fitBounds(bounds, { left: (windowWidth > 800 ? 420 : 0) });

        }, [results]);
    };

    const handleBoundsChange = useCallback((ev) => {
        if (ev.detail.zoom > 15) {
            ev.map.setZoom(15);
        }
    });

    useEffect(() => {
        async function doSearch() {
            if (form != null) {
                form.set('OrderBy', order);
                form.set('PageSize', mapPageSize);
                setResults(null);
                const response = await fetch('/ppi/propertysearchapi/dosearch/' + pageId, {
                    method: "POST",
                    body: form
                });
                const data = await response.json();
                window.history.replaceState(null, null, baseUrl + '?map=true&' + new URLSearchParams(Array.from(form)));
                setResults(data);
            }
        }
        doSearch();
    }, [form, order]);


    useEffect(() => {
        function handleResize() {
            setWindowWidth(window.innerWidth);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    function sortChanged(evt) {
        setSortOrder(evt.target.value);
    }

    const ukBounds = {
        north: 61.5471111,
        east: 9.5844157,
        south: 47.5554486,
        west: -18.5319589,
        padding: {
            left: 500
        }
    }

    function markerClick(e, id) {
        id = id || e.domEvent?.target.getAttribute('id');
        if (id != null) {
            document.querySelectorAll('.property-result, .map-marker').forEach((el) => {
                el.classList.remove('active');
            });
            e.domEvent.target.classList.add('active');
            const result = document.getElementById('p_' + id);
            if (result != null) {
                result.firstChild.classList.add('active');
                const inner = document.getElementsByClassName('results-inner')[0];
                inner.scrollTo(0, result.offsetTop - (inner.offsetHeight - result.offsetHeight) / 2);
            }
        }
    }

    function getMapBounds() {
        if (form != null && form.Bounds != null) {
            const bp = form.Bounds.split(',');
            return {
                north: bp[0], east: bp[1], south: [bp2], west: bp[3], padding: { left: 400 }
            };
        } else {
            return ukBounds;
        }
    }

    return (
        <>
            <SearchForm config={config} onChange={() => setForm(new FormData(document.getElementById(formId)))} formId={formId} isMobile={windowWidth <= 800} isMap={true} mapResults={results} heading={content.heading} />
            <div id="propertySearchMap">
                <div id="theMap">
                    <APIProvider apiKey={config.GmKey}>
                        <Map defaultBounds={ukBounds} defaultZoom={5} mapId="bf6534rgf" reuseMaps={true} onBoundsChanged={handleBoundsChange} >
                            {results != null &&
                                results.Properties.map((p, i) =>
                                     <AdvancedMarker key={p.Id} position={{ lat: p.Lat, lng: p.Lng }} title={p.Heading} onClick={(e) => { markerClick(e); }}>
                                        <div className={'map-marker ' + config.PropertySearchType + (i == 0 ? ' active' : '')} alt={p.Heading} height="45" width="33" id={p.Id} >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 33 45" fill="none">
                                                <path d="M33 16.3047C33 7.29956 25.6122 0 16.5018 0C7.39153 0 0 7.29956 0 16.3047C0 19.938 1.2061 23.2945 3.2384 26.0058L3.2089 26.035L16.4982 45L29.7874 26.035L29.7579 26.0058C31.7939 23.2945 32.9963 19.938 32.9963 16.3047H33Z" />
                                            </svg>
                                        </div>
                                    </AdvancedMarker>
                                )
                            }
                        </Map>
                        <MapBoundsComponent results={results} />
                        {config.PropertySearchType == "bng" &&
                            <>
                            <MapDataOverlays config={config} ncaState={ncaState} lpaState={lpaState} onHover={(f) => {
                                document.getElementById('bngLabelInner').innerText = f;
                                if (f != '') document.getElementById('bngLabel').classList.remove('hide'); else document.getElementById('bngLabel').classList.add('hide');
                            }
                            } />
                            <MapControl position={ControlPosition.BOTTOM_LEFT}>
                                <div id="bngLabel" className="bng-label hide"><p id="bngLabelInner"></p></div>
                            </MapControl>
                            <MapControl position={ControlPosition.TOP_LEFT}>
                                <div className="bng-controls">
                                    <label>
                                        <input type="checkbox" onChange={() => setNcaState(!ncaState)} /> Show National Character Areas
                                    </label>
                                    <label>
                                        <input type="checkbox" onChange={() => setLpaState(!lpaState)} /> Show Local Planning Authorities
                                    </label>
                                </div>
                            </MapControl>
                            </>
                        }
                        <MapControl position={ControlPosition.TOP_RIGHT}>
                            <a href="#" className="bg-white btn-list-view" onClick={(e) => { e.preventDefault(); window.location = window.location.href.replace('?map=true','?map=false').replace('&PageSize=' + mapPageSize,''); }} title="List view">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="14" viewBox="0 0 20 14" fill="none">
                                    <line x1="6" y1="1" x2="20" y2="1" stroke="black" strokeWidth="2" />
                                    <line x1="6" y1="7" x2="20" y2="7" stroke="black" strokeWidth="2" />
                                    <line x1="6" y1="13" x2="20" y2="13" stroke="black" strokeWidth="2" />
                                    <line y1="1" x2="4" y2="1" stroke="black" strokeWidth="2" />
                                    <line y1="7" x2="4" y2="7" stroke="black" strokeWidth="2" />
                                    <line y1="13" x2="4" y2="13" stroke="black" strokeWidth="2" />
                                </svg>
                                List view
                            </a>
                        </MapControl>
                    </APIProvider>
                </div>
                <div className="results-list">
                    {results == null && <div className="loading-spinner"/> }
                    {results != null && results.TotalCount == 0 &&
                        <div dangerouslySetInnerHTML={({ __html: content.noResults })}></div>
                    }
                    {results != null &&
                        <div className="results-inner">
                            {results.Properties.map((p, i) => <PropertyResult property={p} key={p.Id} isActive={i==0} />)}
                        </div>
                    }
                </div>
            </div>
        </>
    );
}